import { isNumber } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { getEffectiveScanValue, ScanValue } from '@kaiizen/kai-scan-template-system';
import { useCallback, useEffect, useState } from 'react';

import { formatUsd } from '@/Client/Presenters';
import { LAYOUT2, TYPOGRAPHY2 } from '@/Client/Styles';
import { Button } from '@/Client/Widgets';

const isUsdWithinTolerance = (left: number, right: number, tolerance: number = 0.001) => Math.abs(left - right) < tolerance;

/**
 * Returns true when the line item's effective USD price is missing or differs from the catalog price.
 */
const catalogUsdPriceNeedsSyncToLineItem = (catalogPrice: number, priceValue: ScanValue | undefined): boolean => {
  const effectivePrice = getEffectiveScanValue(priceValue);
  if (effectivePrice == null) return true;
  if (typeof effectivePrice !== 'number') return true;
  return !isUsdWithinTolerance(effectivePrice, catalogPrice);
};

export const shouldShowApproveCatalogPriceChange = (params: {
  scanIsEditable: boolean;
  lineItemHasPrice: boolean;
  priceIsLive: boolean;
  defaultPrice: number | undefined;
  lineItemId: string | undefined;
  optionId: string | undefined;
  lineItemPrice: ScanValue | undefined;
}): boolean => {
  const { scanIsEditable, lineItemHasPrice, priceIsLive, defaultPrice, lineItemId, optionId, lineItemPrice } = params;
  if (!scanIsEditable) return false;
  if (!lineItemHasPrice) return false;
  if (!priceIsLive) return false;
  if (!isNumber(defaultPrice)) return false;
  if (!lineItemId || !optionId) return false;
  if (!catalogUsdPriceNeedsSyncToLineItem(defaultPrice, lineItemPrice)) return false;
  return true;
};

const MODAL_OVERLAY_STYLE = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  z-index: 2000;
  padding: 20px;
`;

const MODAL_CONTENT_STYLE = css`
  background: var(--background);
  padding: ${LAYOUT2.spacing3}px;
  width: min(480px, calc(100vw - 120px));
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 10px 20px rgba(0, 0, 0, 0.15);
`;

const MODAL_TITLE_STYLE = css`
  ${TYPOGRAPHY2.largeBold}
  color: var(--text);
  margin-bottom: ${LAYOUT2.spacing2}px;
`;

const MODAL_BODY_STYLE = css`
  ${TYPOGRAPHY2.small}
  color: var(--text);
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1p5}px;
`;

const MODAL_PRICE_ROW_STYLE = css`
  ${TYPOGRAPHY2.smallBold}
  color: var(--text);
`;

const MODAL_BUTTONS_STYLE = css`
  display: flex;
  justify-content: flex-end;
  gap: ${LAYOUT2.spacing1}px;
  margin-top: ${LAYOUT2.spacing2}px;
`;

const MODAL_BUTTON_STYLE = css`
  min-width: 120px;
`;

/** Full-width row above Parts/Labor so the action does not widen the price flex row or cause horizontal scroll. */
const APPROVE_ROW_STYLE = css`
  width: 100%;
  min-width: 0;
  align-self: flex-start;
  margin-bottom: ${LAYOUT2.spacingHalf}px;
`;

const APPROVE_PRICE_BUTTON_WRAP_STYLE = css`
  max-width: 100%;
  & button {
    max-width: 100%;
    white-space: normal;
    line-height: 1.25;
    text-align: center;
    width: auto;
  }
`;

const ApproveCatalogPriceChangeModal = ({
  open,
  lineItemPrice,
  catalogUsd,
  onApprove,
  onClose,
}: {
  open: boolean;
  lineItemPrice: ScanValue | undefined;
  catalogUsd: number;
  onApprove: () => void;
  onClose: () => void;
}) => {
  const effectivePrice = getEffectiveScanValue(lineItemPrice);
  const currentDisplay = typeof effectivePrice === 'number' ? formatUsd(effectivePrice) : 'Not set';
  const catalogDisplay = formatUsd(catalogUsd);

  const handleApprove = useCallback(() => {
    onApprove();
    onClose();
  }, [onApprove, onClose]);

  if (!open) return null;

  return (
    <div css={MODAL_OVERLAY_STYLE} onClick={onClose}>
      <div css={MODAL_CONTENT_STYLE} onClick={event => event.stopPropagation()}>
        <div css={MODAL_TITLE_STYLE}>Update part price from catalog</div>
        <div css={MODAL_BODY_STYLE}>
          <p>
            This line item uses a live catalog product. The catalog price for this SKU has changed, so the line item part price no longer matches the current
            catalog price. You can approve the update to align the line item with the catalog.
          </p>
          <div css={MODAL_PRICE_ROW_STYLE}>Current line item price: {currentDisplay}</div>
          <div css={MODAL_PRICE_ROW_STYLE}>New catalog price: {catalogDisplay}</div>
        </div>
        <div css={MODAL_BUTTONS_STYLE}>
          <Button buttonStyle='white' onClick={onClose} css={MODAL_BUTTON_STYLE}>
            Cancel
          </Button>
          <Button buttonStyle='green' onClick={handleApprove} css={MODAL_BUTTON_STYLE}>
            Approve
          </Button>
        </div>
      </div>
    </div>
  );
};

const ApproveCatalogPriceChangeButton = ({ onClick }: { onClick: () => void }) => (
  <div css={APPROVE_PRICE_BUTTON_WRAP_STYLE}>
    <Button buttonStyle='yellow' onClick={onClick}>
      Found Catalog Price Changes
    </Button>
  </div>
);

export const ApproveCatalogPriceChange = ({
  visible,
  lineItemPrice,
  catalogUsd,
  onApprove,
}: {
  visible: boolean;
  lineItemPrice: ScanValue | undefined;
  catalogUsd: number;
  onApprove: () => void;
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!visible) setModalOpen(false);
  }, [visible]);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  if (!visible) return null;

  return (
    <>
      <div css={APPROVE_ROW_STYLE}>
        <ApproveCatalogPriceChangeButton onClick={openModal} />
      </div>
      <ApproveCatalogPriceChangeModal
        open={modalOpen}
        lineItemPrice={lineItemPrice}
        catalogUsd={catalogUsd}
        onApprove={onApprove}
        onClose={closeModal}
      />
    </>
  );
};
